import { useEffect, useState, useCallback } from 'react'
import { Link } from 'react-router-dom'
import { DENOMINATION_OPTIONS } from '@fellowship/shared'
import { getEventType } from '../../components/ui/eventTypes'
import {
  useAdminChurches,
  useAdminCreateChurch,
  useAdminUpdateChurch,
  useAdminDeleteChurch,
  useAdminRestoreChurch,
  useAdminChurchDetail,
  useAdminAddChurchMember,
  useAdminRemoveChurchMember,
  useAdminUpdateChurchMember,
} from '../../hooks/useAdmin'
import { ConfirmModal } from '../../components/ui/ConfirmModal'
import { AdminModal, adminFieldClass } from '../../components/admin/AdminModal'
import type { AdminChurch, AdminChurchDetail } from '../../types/admin'

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

const ROLE_LABELS: Record<string, string> = {
  owner: 'Owner',
  coordinator: 'Coordinator',
  viewer: 'Viewer',
}

const ROLE_COLORS: Record<string, { bg: string; color: string }> = {
  owner: { bg: '#FFF3CD', color: '#856404' },
  coordinator: { bg: '#D1ECF1', color: '#0C5460' },
  viewer: { bg: 'var(--surface-2)', color: 'var(--muted)' },
}

const stripeBase = 'https://dashboard.stripe.com'

type ChurchFormState = {
  name: string
  slug: string
  addressLine1: string
  city: string
  state: string
  zip: string
  locationLat: string
  locationLng: string
  denomination: string
  phone: string
  email: string
  websiteUrl: string
  verified: boolean
  claimed: boolean
  ownerUserId: string
}

const emptyChurchForm = (): ChurchFormState => ({
  name: '',
  slug: '',
  addressLine1: '',
  city: '',
  state: '',
  zip: '',
  locationLat: '45.52',
  locationLng: '-122.68',
  denomination: 'seventh-day-adventist',
  phone: '',
  email: '',
  websiteUrl: '',
  verified: false,
  claimed: true,
  ownerUserId: '',
})

function churchToForm(c: AdminChurch | AdminChurchDetail): ChurchFormState {
  return {
    name: c.name,
    slug: c.slug,
    addressLine1: c.addressLine1 ?? '',
    city: c.city ?? '',
    state: c.state ?? '',
    zip: '',
    locationLat: String(c.locationLat),
    locationLng: String(c.locationLng),
    denomination: c.denomination,
    phone: c.phone ?? '',
    email: c.email ?? '',
    websiteUrl: c.websiteUrl ?? '',
    verified: c.verified,
    claimed: c.claimed,
    ownerUserId: '',
  }
}

// ─── Church detail drawer ─────────────────────────────────────────────────────

function ChurchDetailDrawer({
  churchId,
  onClose,
}: {
  churchId: string
  onClose: () => void
}) {
  const { data, isLoading } = useAdminChurchDetail(churchId)
  const updateChurch = useAdminUpdateChurch()
  const updateMember = useAdminUpdateChurchMember()
  const addMember = useAdminAddChurchMember()
  const removeMember = useAdminRemoveChurchMember()
  const church = data?.data

  const [form, setForm] = useState<ChurchFormState>(emptyChurchForm())
  const [newMemberUserId, setNewMemberUserId] = useState('')
  const [newMemberRole, setNewMemberRole] = useState('coordinator')

  useEffect(() => {
    if (church) setForm(churchToForm(church))
  }, [church])

  function handleSaveChurch() {
    updateChurch.mutate({
      id: churchId,
      name: form.name.trim(),
      slug: form.slug.trim() || undefined,
      addressLine1: form.addressLine1 || undefined,
      city: form.city || undefined,
      state: form.state || undefined,
      zip: form.zip || undefined,
      locationLat: parseFloat(form.locationLat),
      locationLng: parseFloat(form.locationLng),
      denomination: form.denomination,
      phone: form.phone || null,
      email: form.email || null,
      websiteUrl: form.websiteUrl || undefined,
      verified: form.verified,
      claimed: form.claimed,
    })
  }

  function handleAddMember() {
    if (!newMemberUserId.trim()) return
    addMember.mutate(
      { churchId, userId: newMemberUserId.trim(), role: newMemberRole },
      { onSuccess: () => setNewMemberUserId('') },
    )
  }

  return (
    <div className="fixed inset-0 z-[500] flex items-start justify-end" onClick={onClose}>
      <div className="absolute inset-0 bg-black/35" />
      <div
        className="relative z-[1] w-[520px] h-screen overflow-y-auto bg-surface flex flex-col gap-5 px-7 pt-7 pb-10"
        style={{ boxShadow: '-4px 0 32px rgba(0,0,0,0.18)' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0 flex-1">
            <div className="text-[11px] font-semibold text-muted uppercase tracking-[0.08em] mb-1">
              Edit church
            </div>
            {isLoading ? (
              <div className="text-[18px] text-muted">Loading…</div>
            ) : (
              <h2 className="m-0 text-[20px] font-bold">{church?.name}</h2>
            )}
          </div>
          <button type="button" onClick={onClose} aria-label="Close" className="bg-transparent border-0 text-[22px] cursor-pointer text-muted leading-none">
            ×
          </button>
        </div>

        {church && (
          <>
            <div className="flex flex-col gap-3">
              <label className="flex flex-col gap-1 text-[12px] font-semibold text-ink-2">
                Church UUID
                <input
                  readOnly
                  value={church.id}
                  className={`${adminFieldClass()} font-mono text-[12px] text-ink`}
                  onFocus={(e) => e.target.select()}
                />
              </label>
              <label className="flex flex-col gap-1 text-[12px] font-semibold text-ink-2">
                Name
                <input className={adminFieldClass()} value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} />
              </label>
              <label className="flex flex-col gap-1 text-[12px] font-semibold text-ink-2">
                Slug
                <input className={adminFieldClass()} value={form.slug} onChange={(e) => setForm((f) => ({ ...f, slug: e.target.value }))} />
              </label>
              <div className="grid grid-cols-2 gap-3">
                <label className="flex flex-col gap-1 text-[12px] font-semibold text-ink-2">
                  City
                  <input className={adminFieldClass()} value={form.city} onChange={(e) => setForm((f) => ({ ...f, city: e.target.value }))} />
                </label>
                <label className="flex flex-col gap-1 text-[12px] font-semibold text-ink-2">
                  State
                  <input className={adminFieldClass()} value={form.state} onChange={(e) => setForm((f) => ({ ...f, state: e.target.value }))} />
                </label>
              </div>
              <label className="flex flex-col gap-1 text-[12px] font-semibold text-ink-2">
                Address
                <input className={adminFieldClass()} value={form.addressLine1} onChange={(e) => setForm((f) => ({ ...f, addressLine1: e.target.value }))} />
              </label>
              <div className="grid grid-cols-2 gap-3">
                <label className="flex flex-col gap-1 text-[12px] font-semibold text-ink-2">
                  Lat
                  <input className={adminFieldClass()} value={form.locationLat} onChange={(e) => setForm((f) => ({ ...f, locationLat: e.target.value }))} />
                </label>
                <label className="flex flex-col gap-1 text-[12px] font-semibold text-ink-2">
                  Lng
                  <input className={adminFieldClass()} value={form.locationLng} onChange={(e) => setForm((f) => ({ ...f, locationLng: e.target.value }))} />
                </label>
              </div>
              <label className="flex flex-col gap-1 text-[12px] font-semibold text-ink-2">
                Denomination
                <select className={adminFieldClass()} value={form.denomination} onChange={(e) => setForm((f) => ({ ...f, denomination: e.target.value }))}>
                  {DENOMINATION_OPTIONS.map((d) => (
                    <option key={d.key} value={d.key}>{d.label}</option>
                  ))}
                  {form.denomination && !DENOMINATION_OPTIONS.some((d) => d.key === form.denomination) && (
                    <option value={form.denomination}>{form.denomination}</option>
                  )}
                </select>
              </label>
              <div className="grid grid-cols-2 gap-3">
                <label className="flex flex-col gap-1 text-[12px] font-semibold text-ink-2">
                  Phone
                  <input className={adminFieldClass()} value={form.phone} onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))} />
                </label>
                <label className="flex flex-col gap-1 text-[12px] font-semibold text-ink-2">
                  Email
                  <input className={adminFieldClass()} type="email" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} />
                </label>
              </div>
              <label className="flex flex-col gap-1 text-[12px] font-semibold text-ink-2">
                Website
                <input className={adminFieldClass()} value={form.websiteUrl} onChange={(e) => setForm((f) => ({ ...f, websiteUrl: e.target.value }))} />
              </label>
              <div className="flex gap-4">
                <label className="flex items-center gap-2 text-[13px] cursor-pointer">
                  <input type="checkbox" checked={form.verified} onChange={(e) => setForm((f) => ({ ...f, verified: e.target.checked }))} />
                  Verified
                </label>
                <label className="flex items-center gap-2 text-[13px] cursor-pointer">
                  <input type="checkbox" checked={form.claimed} onChange={(e) => setForm((f) => ({ ...f, claimed: e.target.checked }))} />
                  Claimed
                </label>
              </div>
              <button
                type="button"
                onClick={handleSaveChurch}
                disabled={updateChurch.isPending}
                className="self-start px-4 py-2 rounded-lg border-0 bg-[#1a1a2e] text-white text-[13px] font-semibold cursor-pointer font-sans disabled:opacity-50"
              >
                {updateChurch.isPending ? 'Saving…' : 'Save church'}
              </button>
            </div>

            <div>
              <div className="text-[12px] font-semibold text-ink-2 mb-2.5">
                Events ({church.events.length})
              </div>
              {church.events.length === 0 ? (
                <div className="text-[13px] text-muted">No events linked to this church.</div>
              ) : (
                <div className="flex flex-col gap-1.5 max-h-[280px] overflow-y-auto">
                  {church.events.map((ev) => {
                    const t = getEventType(ev.eventType as Parameters<typeof getEventType>[0])
                    return (
                      <Link
                        key={ev.id}
                        to={`/events/${ev.id}`}
                        className="flex items-center justify-between gap-3 px-3.5 py-2.5 rounded-[10px] bg-s2 border border-line-soft no-underline text-inherit hover:border-primary/40"
                      >
                        <div className="min-w-0 flex-1">
                          <div className="text-[13px] font-semibold text-primary truncate">{ev.title}</div>
                          <div className="text-[10px] text-muted font-mono mt-0.5 truncate">{ev.id}</div>
                        </div>
                        <div className="flex flex-col items-end gap-1 shrink-0">
                          <span
                            className="px-2 py-0.5 rounded-md text-[10px] font-semibold"
                            style={{ background: t.soft, color: t.color }}
                          >
                            {t.label}
                          </span>
                          <span className="text-[10px] text-muted">{ev.status} · {ev.source}</span>
                        </div>
                      </Link>
                    )
                  })}
                </div>
              )}
            </div>

            {(church.stripeCustomerId || church.stripeSubscriptionId) && (
              <div>
                <div className="text-[12px] font-semibold text-ink-2 mb-2">Stripe</div>
                <div className="flex gap-2 flex-wrap">
                  {church.stripeCustomerId && (
                    <a href={`${stripeBase}/customers/${church.stripeCustomerId}`} target="_blank" rel="noopener noreferrer" className="px-2.5 py-1 rounded-md text-[12px] font-semibold no-underline" style={{ background: '#EEF0FF', color: '#635bff', border: '1px solid #C7CAFE' }}>
                      Customer ↗
                    </a>
                  )}
                  {church.stripeSubscriptionId && (
                    <a href={`${stripeBase}/subscriptions/${church.stripeSubscriptionId}`} target="_blank" rel="noopener noreferrer" className="px-2.5 py-1 rounded-md text-[12px] font-semibold no-underline" style={{ background: '#EEF0FF', color: '#635bff', border: '1px solid #C7CAFE' }}>
                      Subscription ↗
                    </a>
                  )}
                </div>
              </div>
            )}

            <div>
              <div className="text-[12px] font-semibold text-ink-2 mb-2">Add staff member</div>
              <div className="flex gap-2">
                <input
                  className="flex-1 min-w-0 px-3 py-2 rounded-lg border border-line-soft bg-bg text-[12px] font-mono box-border"
                  placeholder="User UUID"
                  value={newMemberUserId}
                  onChange={(e) => setNewMemberUserId(e.target.value)}
                />
                <select
                  className="w-[130px] shrink-0 px-2 py-2 rounded-lg border border-line-soft bg-bg text-[13px] font-sans box-border"
                  value={newMemberRole}
                  onChange={(e) => setNewMemberRole(e.target.value)}
                >
                  <option value="owner">Owner</option>
                  <option value="coordinator">Coordinator</option>
                  <option value="viewer">Viewer</option>
                </select>
                <button type="button" onClick={handleAddMember} disabled={addMember.isPending} className="px-3 py-2 rounded-lg border-0 bg-primary text-on-primary text-[12px] font-semibold cursor-pointer font-sans shrink-0">
                  Add
                </button>
              </div>
            </div>

            <div>
              <div className="text-[12px] font-semibold text-ink-2 mb-2.5">
                Staff & admins ({church.admins.length})
              </div>
              {church.admins.length === 0 ? (
                <div className="text-[13px] text-muted">No admins on record.</div>
              ) : (
                <div className="flex flex-col gap-1.5">
                  {church.admins.map((admin) => {
                    const roleStyle = ROLE_COLORS[admin.role] ?? ROLE_COLORS.viewer
                    return (
                      <div key={admin.userId} className="flex items-center justify-between px-3.5 py-2.5 rounded-[10px] bg-s2 border border-line-soft gap-3">
                        <div className="min-w-0">
                          <div className="text-[13px] font-semibold text-ink">{admin.displayName}</div>
                          <div className="text-[11px] text-muted mt-px truncate">{admin.email}</div>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          <span className="px-2 py-0.5 rounded-md text-[11px] font-semibold" style={{ background: roleStyle.bg, color: roleStyle.color }}>
                            {ROLE_LABELS[admin.role] ?? admin.role}
                          </span>
                          <select
                            value={admin.role}
                            aria-label={`Change role for ${admin.displayName}`}
                            onChange={(e) => updateMember.mutate({ churchId: church.id, userId: admin.userId, role: e.target.value })}
                            className="text-[12px] px-1.5 py-0.5 rounded-md border border-line bg-surface text-ink cursor-pointer font-sans"
                          >
                            <option value="owner">Owner</option>
                            <option value="coordinator">Coordinator</option>
                            <option value="viewer">Viewer</option>
                          </select>
                          <button
                            type="button"
                            onClick={() => removeMember.mutate({ churchId: church.id, userId: admin.userId })}
                            className="text-[11px] text-danger-deep cursor-pointer bg-transparent border-0 font-sans"
                          >
                            Remove
                          </button>
                        </div>
                      </div>
                    )
                  })}
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  )
}

// ─── Main page ────────────────────────────────────────────────────────────────

export default function AdminChurchesPage() {
  const [page, setPage] = useState(1)
  const [confirmDelete, setConfirmDelete] = useState<AdminChurch | null>(null)
  const [detailId, setDetailId] = useState<string | null>(null)
  const [creating, setCreating] = useState(false)
  const [createForm, setCreateForm] = useState<ChurchFormState>(emptyChurchForm())
  const [showDeleted, setShowDeleted] = useState(false)
  const [searchInput, setSearchInput] = useState('')
  const [q, setQ] = useState('')
  const [denomination, setDenomination] = useState('')
  const [sort, setSort] = useState('created_at')
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc')

  const debounceSearch = useCallback((value: string) => {
    const t = setTimeout(() => { setQ(value); setPage(1) }, 300)
    return () => clearTimeout(t)
  }, [])

  useEffect(() => debounceSearch(searchInput), [searchInput, debounceSearch])

  const { data, isLoading } = useAdminChurches({ page, showDeleted, q, denomination: denomination || undefined, sort, sortDir })
  const createChurch = useAdminCreateChurch()
  const deleteChurch = useAdminDeleteChurch()
  const restoreChurch = useAdminRestoreChurch()

  const churches = data?.data ?? []
  const total = data?.meta.total ?? 0
  const totalPages = Math.ceil(total / 20)

  function handleCreate() {
    createChurch.mutate(
      {
        name: createForm.name.trim(),
        addressLine1: createForm.addressLine1,
        city: createForm.city,
        state: createForm.state,
        zip: createForm.zip || '00000',
        locationLat: parseFloat(createForm.locationLat),
        locationLng: parseFloat(createForm.locationLng),
        denomination: createForm.denomination,
        phone: createForm.phone || undefined,
        websiteUrl: createForm.websiteUrl || undefined,
        claimed: createForm.claimed,
        ownerUserId: createForm.ownerUserId.trim() || undefined,
      },
      { onSuccess: () => setCreating(false) },
    )
  }

  function handleDelete(church: AdminChurch) {
    deleteChurch.mutate(church.id, {
      onSuccess: () => {
        setConfirmDelete(null)
        if (detailId === church.id) setDetailId(null)
      },
    })
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="m-0 text-[24px] font-bold">Churches</h1>
          <div className="text-[13px] text-muted mt-1">{total} total</div>
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => { setShowDeleted((v) => !v); setPage(1) }}
            className={`h-9 px-3.5 rounded-lg border text-[13px] font-semibold cursor-pointer font-sans ${showDeleted ? 'bg-amber-50 border-amber-300 text-amber-800' : 'border-line-soft bg-surface text-ink-2'}`}
          >
            {showDeleted ? 'Hide deleted' : 'Show deleted'}
          </button>
          {!showDeleted && (
            <button
              type="button"
              onClick={() => { setCreateForm(emptyChurchForm()); setCreating(true) }}
              className="h-9 px-4 rounded-lg border-0 bg-[#1a1a2e] text-white text-[13px] font-semibold cursor-pointer font-sans"
            >
              + Add church
            </button>
          )}
        </div>
      </div>

      <div className="flex gap-2 mb-4 flex-wrap">
        <input
          type="search"
          placeholder="Search churches…"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          className="h-9 px-3 rounded-lg border border-line-soft bg-surface text-[13px] text-ink font-sans flex-1 min-w-[180px]"
        />
        <select
          value={denomination}
          onChange={(e) => { setDenomination(e.target.value); setPage(1) }}
          className="h-9 px-3 rounded-lg border border-line-soft bg-surface text-[13px] text-ink font-sans cursor-pointer"
        >
          <option value="">All denominations</option>
          {DENOMINATION_OPTIONS.map((d) => (
            <option key={d.key} value={d.key}>{d.label}</option>
          ))}
        </select>
        <select
          value={`${sort}:${sortDir}`}
          onChange={(e) => {
            const [s, d] = e.target.value.split(':')
            setSort(s)
            setSortDir(d as 'asc' | 'desc')
            setPage(1)
          }}
          className="h-9 px-3 rounded-lg border border-line-soft bg-surface text-[13px] text-ink font-sans cursor-pointer"
        >
          <option value="created_at:desc">Newest first</option>
          <option value="created_at:asc">Oldest first</option>
          <option value="name:asc">Name A–Z</option>
          <option value="name:desc">Name Z–A</option>
          <option value="denomination:asc">Denomination A–Z</option>
        </select>
      </div>

      {isLoading ? (
        <div className="text-muted text-[14px] py-10">Loading…</div>
      ) : (
        <div className="bg-surface rounded-[14px] border border-line-soft overflow-hidden">
          <table className="w-full border-collapse text-[13px]">
            <thead>
              <tr className="border-b border-line-soft bg-s2">
                {['Name', 'Location', 'Denomination', 'Plan', 'Status', 'Joined', 'Actions'].map((h) => (
                  <th key={h} className="px-4 py-2.5 text-left font-semibold text-ink-2 text-[12px]">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {churches.map((c, i) => (
                <tr key={c.id} className={i < churches.length - 1 ? 'border-b border-line-soft' : ''}>
                  <td className="px-4 py-3 font-medium">
                    <Link to={`/churches/${c.slug}`} className="no-underline block max-w-[240px]">
                      <div className="text-primary font-semibold text-[13px] hover:underline">{c.name}</div>
                      <div className="text-[11px] text-muted mt-0.5">/{c.slug}</div>
                    </Link>
                  </td>
                  <td className="px-4 py-3 text-ink-2">{[c.city, c.state].filter(Boolean).join(', ') || '—'}</td>
                  <td className="px-4 py-3 text-ink-2">{c.denomination}</td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-0.5 rounded-md text-[11px] font-semibold ${c.subscriptionPlan ? 'bg-primary-soft text-primary' : 'bg-s2 text-muted'}`}>
                      {c.subscriptionPlan ?? 'free'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-[12px]">
                    {c.verified && <span className="text-[#1e7e34]">✓ Verified </span>}
                    {!c.claimed && <span className="text-muted">Unclaimed</span>}
                    {c.verified && c.claimed && <span className="text-muted">—</span>}
                  </td>
                  <td className="px-4 py-3 text-muted">{formatDate(c.createdAt)}</td>
                  <td className="px-4 py-3">
                    <div className="flex gap-2">
                      {c.deletedAt ? (
                        <button
                          type="button"
                          onClick={() => restoreChurch.mutate(c.id)}
                          disabled={restoreChurch.isPending}
                          className="px-2.5 py-1 rounded-md text-[12px] cursor-pointer font-sans border border-good bg-good-soft text-good-deep disabled:opacity-50"
                        >
                          Restore
                        </button>
                      ) : (
                        <>
                          <button type="button" onClick={() => setDetailId(c.id)} className="px-2.5 py-1 rounded-md text-[12px] cursor-pointer font-sans border border-line-soft bg-s2 text-ink">
                            Edit
                          </button>
                          <button type="button" onClick={() => setConfirmDelete(c)} className="px-2.5 py-1 rounded-md text-[12px] cursor-pointer font-sans border border-danger-line bg-danger-soft text-danger-deep">
                            Delete
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {totalPages > 1 && (
        <div className="flex gap-2 mt-5 justify-end">
          <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1} className="px-3 py-1.5 rounded-lg border border-line-soft bg-surface text-ink text-[13px] cursor-pointer font-sans disabled:opacity-40">← Prev</button>
          <span className="text-[13px] text-muted self-center">Page {page} of {totalPages}</span>
          <button onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page === totalPages} className="px-3 py-1.5 rounded-lg border border-line-soft bg-surface text-ink text-[13px] cursor-pointer font-sans disabled:opacity-40">Next →</button>
        </div>
      )}

      {detailId && <ChurchDetailDrawer churchId={detailId} onClose={() => setDetailId(null)} />}

      {creating && (
        <AdminModal
          title="Add church"
          onClose={() => setCreating(false)}
          wide
          footer={
            <>
              <button type="button" onClick={() => setCreating(false)} className="px-4 py-2 rounded-lg border border-line-soft bg-surface text-[13px] cursor-pointer font-sans">Cancel</button>
              <button type="button" onClick={handleCreate} disabled={createChurch.isPending || !createForm.name} className="px-4 py-2 rounded-lg border-0 bg-[#1a1a2e] text-white text-[13px] font-semibold cursor-pointer font-sans disabled:opacity-50">
                {createChurch.isPending ? 'Creating…' : 'Create'}
              </button>
            </>
          }
        >
          <label className="flex flex-col gap-1 text-[12px] font-semibold text-ink-2">
            Name *
            <input className={adminFieldClass()} value={createForm.name} onChange={(e) => setCreateForm((f) => ({ ...f, name: e.target.value }))} />
          </label>
          <label className="flex flex-col gap-1 text-[12px] font-semibold text-ink-2">
            Address line 1 *
            <input className={adminFieldClass()} value={createForm.addressLine1} onChange={(e) => setCreateForm((f) => ({ ...f, addressLine1: e.target.value }))} />
          </label>
          <div className="grid grid-cols-3 gap-3">
            <label className="flex flex-col gap-1 text-[12px] font-semibold text-ink-2">
              City *
              <input className={adminFieldClass()} value={createForm.city} onChange={(e) => setCreateForm((f) => ({ ...f, city: e.target.value }))} />
            </label>
            <label className="flex flex-col gap-1 text-[12px] font-semibold text-ink-2">
              State *
              <input className={adminFieldClass()} value={createForm.state} onChange={(e) => setCreateForm((f) => ({ ...f, state: e.target.value }))} />
            </label>
            <label className="flex flex-col gap-1 text-[12px] font-semibold text-ink-2">
              ZIP *
              <input className={adminFieldClass()} value={createForm.zip} onChange={(e) => setCreateForm((f) => ({ ...f, zip: e.target.value }))} />
            </label>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <label className="flex flex-col gap-1 text-[12px] font-semibold text-ink-2">
              Lat *
              <input className={adminFieldClass()} value={createForm.locationLat} onChange={(e) => setCreateForm((f) => ({ ...f, locationLat: e.target.value }))} />
            </label>
            <label className="flex flex-col gap-1 text-[12px] font-semibold text-ink-2">
              Lng *
              <input className={adminFieldClass()} value={createForm.locationLng} onChange={(e) => setCreateForm((f) => ({ ...f, locationLng: e.target.value }))} />
            </label>
          </div>
          <label className="flex flex-col gap-1 text-[12px] font-semibold text-ink-2">
            Denomination
            <select className={adminFieldClass()} value={createForm.denomination} onChange={(e) => setCreateForm((f) => ({ ...f, denomination: e.target.value }))}>
              {DENOMINATION_OPTIONS.map((d) => (
                <option key={d.key} value={d.key}>{d.label}</option>
              ))}
            </select>
          </label>
          <label className="flex flex-col gap-1 text-[12px] font-semibold text-ink-2">
            Owner user ID <span className="font-normal text-muted">(optional)</span>
            <input className={adminFieldClass()} value={createForm.ownerUserId} onChange={(e) => setCreateForm((f) => ({ ...f, ownerUserId: e.target.value }))} />
          </label>
          <label className="flex items-center gap-2 text-[13px] cursor-pointer">
            <input type="checkbox" checked={createForm.claimed} onChange={(e) => setCreateForm((f) => ({ ...f, claimed: e.target.checked }))} />
            Claimed listing
          </label>
        </AdminModal>
      )}

      {confirmDelete && (
        <ConfirmModal
          title="Delete church?"
          body={<><strong>{confirmDelete.name}</strong> will be soft-deleted and hidden from the public site. It can be restored from the deleted churches view.</>}
          confirmLabel="Delete"
          pendingLabel="Deleting…"
          onConfirm={() => handleDelete(confirmDelete)}
          onCancel={() => setConfirmDelete(null)}
          isPending={deleteChurch.isPending}
        />
      )}
    </div>
  )
}
